import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { debounce, once } from 'lodash-es';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import isEqual from 'react-fast-compare';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  ReportConfigV2,
  useReportConfigActionsV2,
  useReportPermissions,
} from 'src/hooks/reportConfigs';
import { DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import {
  ReportMetricsSummaryV2,
  ReportMetricsV2,
  ReportValueType,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReportMetricsResponse = { [key: string]: any };

export type ReportValueTypesReponse = { [key: string]: ReportValueType };

export type IReportMetricsContext = {
  /**
   * Original report config that serves as the persisted report config.
   */
  originalReportConfig: ReportConfigV2;
  /**
   * Temporary report config that is used to get the report metrics during editing/viewing mode
   * and is persisted depending on the user's permissions and the actual action.
   */
  tempReportConfig: ReportConfigV2;
  updateTempReportConfig: (
    reportConfig: ReportConfigV2,
    shouldUpdateReportConfig?: boolean
  ) => void;
  isLoading: boolean;
  reportMetrics?: ReportMetricsResponse[];
  reportSummary?: ReportMetricsResponse;
  reportValueTypes?: ReportValueTypesReponse;
  refreshMetrics: () => void;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isReportConfigEmpty?: boolean;
  isReportFilterDirty: boolean;
  isReportDirty: boolean;
};

export const createReportMetricsContextV2 = once(() =>
  React.createContext({} as IReportMetricsContext)
);

export function useReportMetricsContextV2() {
  return useContext(createReportMetricsContextV2());
}

export function ReportMetricsContextProviderV2<
  TQuery extends QueryWithViewMode,
>({
  reportConfig,
  queryKey,
  getReportMetrics,
  getReportMetricsSummary,
  children,
  disabled,
}: PropsWithChildren<{
  reportConfig: ReportConfigV2;
  queryKey: string;
  getReportMetrics?: (
    tempReportConfig: ReportConfigV2,
    query: TQuery,
    pageNumber: number
  ) => Promise<ReportMetricsV2>;
  getReportMetricsSummary?: (
    tempReportConfig: ReportConfigV2,
    query: TQuery
  ) => Promise<ReportMetricsSummaryV2>;
  disabled?: boolean;
}>) {
  const ReportMetricsContext = createReportMetricsContextV2();

  const { trackError } = useClientLogContext();
  const { canUpdateReport } = useReportPermissions(reportConfig);

  /**
   * Clarification:
   * tempReportConfig is the temporary report config state that is used to actually get the report metrics.
   * reportConfig is the original report config that is passed as a prop to the component.
   *
   * We want to keep the original report config intact and only update the temporary one.
   * Some of the users don't have the permission to update the report config, but we allow them to
   * update the report metrics without persisting the changes.
   */
  const [tempReportConfig, setTempReportConfig] = React.useState(reportConfig);

  // If ever the report config changes, we need to update the tempReportConfig
  useEffect(() => {
    setTempReportConfig({
      ...reportConfig,
      request: {
        ...reportConfig.request,
        filters: tempReportConfig.request.filters,
        orderBy: tempReportConfig.request.orderBy,
        orderByAsc: tempReportConfig.request.orderByAsc,
        useAccountCurrency: tempReportConfig.request.useAccountCurrency,
      },
    } as ReportConfigV2);

    // Always - only listen to reportConfig
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig]);

  const { activeAccountWebClientConfig } = useAppContext();
  const { upsertReportConfig } = useReportConfigActionsV2();

  const isReportConfigEmpty = useMemo(() => {
    return (
      tempReportConfig.request.orderBy == null ||
      (!tempReportConfig.request.rowGroupings?.length &&
        !tempReportConfig.request.columnGroupings?.length)
    );
  }, [
    tempReportConfig.request.columnGroupings?.length,
    tempReportConfig.request.orderBy,
    tempReportConfig.request.rowGroupings?.length,
  ]);

  const shouldQuery =
    !disabled &&
    !(!getReportMetrics || !activeAccountWebClientConfig.activeAccountId) &&
    !isReportConfigEmpty;

  const reportMetricsQuery = useInfiniteQuery({
    queryKey: [
      `${queryKey}-report-metrics`,
      activeAccountWebClientConfig,
      disabled,
      tempReportConfig.request.aggregations,
      tempReportConfig.request.rowGroupings,
      tempReportConfig.request.columnGroupings,
      tempReportConfig.request.filters,
      tempReportConfig.request.useAccountCurrency,
    ],
    queryFn: async ({ pageParam: pageNumber = 0 }) => {
      if (!shouldQuery) {
        return null;
      }

      const reportMetricsData = await getReportMetrics!(
        tempReportConfig,
        tempReportConfig.request.filters as unknown as TQuery,
        pageNumber
      );

      return {
        reportMetricsData,
        pageNumber,
      };
    },
    getNextPageParam(lastPage) {
      return lastPage?.reportMetricsData.rows.length
        ? lastPage.pageNumber + 1
        : undefined;
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('getReportMetrics', error, {
          ...tempReportConfig,
        });
      },
    },
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  const reportMetricsSummaryQuery = useQuery({
    queryKey: [
      `${queryKey}-report-metrics-summary`,
      activeAccountWebClientConfig,
      disabled,
      tempReportConfig.request.aggregations,
      tempReportConfig.request.rowGroupings,
      tempReportConfig.request.columnGroupings,
      tempReportConfig.request.filters,
      tempReportConfig.request.useAccountCurrency,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      const reportMetricsData = await getReportMetricsSummary!(
        tempReportConfig,
        tempReportConfig.request.filters as unknown as TQuery
      );

      return {
        reportMetricsData,
      };
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('getReportMetricsSummary', error, {
          ...tempReportConfig,
        });
      },
    },
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  const refreshMetrics = useCallback(
    () => reportMetricsQuery.refetch(),
    [reportMetricsQuery]
  );

  const flattenedData = useMemo(
    () =>
      reportMetricsQuery.data?.pages?.flatMap(
        (m) => m?.reportMetricsData?.rows ?? []
      ),
    [reportMetricsQuery.data]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpsertReportConfig = useCallback(
    debounce((report: ReportConfigV2) => {
      if (disabled) {
        return;
      }
      upsertReportConfig(report);
    }, 700),
    [disabled, upsertReportConfig]
  );

  const updateTempReportConfig = useCallback(
    (newReportConfig: ReportConfigV2, shouldUpdateReportConfig = true) => {
      setTempReportConfig(newReportConfig);
      if (canUpdateReport && shouldUpdateReportConfig) {
        // Don't allow updating filters
        debouncedUpsertReportConfig({
          ...newReportConfig,
          request: {
            ...newReportConfig.request,
            filters: reportConfig.request.filters,
          },
        } as ReportConfigV2);
      }
    },
    [canUpdateReport, debouncedUpsertReportConfig, reportConfig.request.filters]
  );

  const isReportFilterDirty = useMemo(
    () =>
      !isEqual(
        tempReportConfig.request.filters,
        reportConfig?.request.filters
      ) ||
      !isEqual(
        tempReportConfig.hiddenFilterItemIds,
        reportConfig?.hiddenFilterItemIds
      ) ||
      !isEqual(
        tempReportConfig.viewableFilterItemIds,
        reportConfig?.viewableFilterItemIds
      ) ||
      !isEqual(
        tempReportConfig.editableFilterItemIds,
        reportConfig?.editableFilterItemIds
      ),
    [
      tempReportConfig.request.filters,
      reportConfig?.request.filters,
      tempReportConfig.hiddenFilterItemIds,
      reportConfig?.hiddenFilterItemIds,
      tempReportConfig.viewableFilterItemIds,
      reportConfig?.viewableFilterItemIds,
      tempReportConfig.editableFilterItemIds,
      reportConfig?.editableFilterItemIds,
    ]
  );

  const isReportDirty = useMemo(
    () => !isEqual(tempReportConfig, reportConfig),
    [tempReportConfig, reportConfig]
  );

  return (
    <ReportMetricsContext.Provider
      value={{
        originalReportConfig: reportConfig,
        tempReportConfig,
        updateTempReportConfig,
        isLoading: reportMetricsQuery.isLoading,
        reportMetrics: flattenedData,
        reportSummary:
          reportMetricsSummaryQuery.data?.reportMetricsData?.row ?? undefined,
        reportValueTypes:
          reportMetricsQuery.data?.pages?.[0]?.reportMetricsData?.valueTypes ??
          reportMetricsSummaryQuery?.data?.reportMetricsData?.valueTypes,
        refreshMetrics: refreshMetrics,
        fetchNextPage: reportMetricsQuery.fetchNextPage,
        hasNextPage: reportMetricsQuery.hasNextPage,
        isFetchingNextPage: reportMetricsQuery.isFetchingNextPage,
        isReportConfigEmpty,
        isReportFilterDirty,
        isReportDirty,
      }}
    >
      {children}
    </ReportMetricsContext.Provider>
  );
}
